import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { DomSanitizer, type SafeHtml } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import * as L from "leaflet";
import type {
  CardOrderMappableAccessPoint,
  CardOrderUpsAccessPoint,
} from "../card-orders.interface";
import {
  formatUpsAccessPointAddress,
  formatUpsAccessPointDistanceKm,
  formatUpsAccessPointName,
  formatUpsAccessPointOpeningHours as localizeUpsAccessPointOpeningHours,
} from "../card-order.util";

const DEFAULT_MAP_CENTER: L.LatLngExpression = [52.2297, 21.0122];
const DEFAULT_MAP_ZOOM = 12;
const FIT_BOUNDS_PADDING: L.PointExpression = [32, 48];
const MOBILE_LAYOUT_QUERY = "(max-width: 900px)";

@Component({
  selector: "app-card-order-access-point",
  templateUrl: "./card-order-access-point.component.html",
  styleUrls: ["./card-order-access-point.component.scss"],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CardOrderAccessPointComponent),
      multi: true,
    },
  ],
})
export class CardOrderAccessPointComponent
  implements AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor
{
  @Input() points: CardOrderUpsAccessPoint[] = [];

  @ViewChild("mapWrap", { static: true })
  private mapWrap?: ElementRef<HTMLDivElement>;

  @ViewChild("mapContainer", { static: true })
  private mapContainer?: ElementRef<HTMLDivElement>;

  @ViewChild("listContainer", { static: true })
  private listContainer?: ElementRef<HTMLDivElement>;

  readonly formatUpsAccessPointName = formatUpsAccessPointName;
  readonly formatUpsAccessPointAddress = formatUpsAccessPointAddress;
  readonly formatUpsAccessPointDistanceKm = formatUpsAccessPointDistanceKm;

  selectedPointId = "";
  disabled = false;

  private map: L.Map | null = null;
  private markersLayer: L.LayerGroup | null = null;
  private readonly markerByPointId = new Map<string, L.Marker>();
  private mapInitialized = false;
  private pendingFitBounds = false;
  private layoutObserver: ResizeObserver | null = null;
  private readonly mobileLayoutQuery = window.matchMedia(MOBILE_LAYOUT_QUERY);
  private readonly onViewportLayoutChange = (): void => {
    this.syncListHeight();
  };
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly translate: TranslateService,
  ) {}

  formatUpsAccessPointOpeningHours(
    value: string | null | undefined,
  ): string | null {
    return localizeUpsAccessPointOpeningHours(
      value,
      (key) => this.translate.instant(key) as string,
    );
  }

  ngAfterViewInit(): void {
    this.initMap();
    this.setupLayoutSync();
    this.mapInitialized = true;
    this.syncMarkers();
    if (this.pendingFitBounds) {
      this.fitMapToPoints();
      this.pendingFitBounds = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.mapInitialized) {
      if (changes["points"]) {
        this.pendingFitBounds = true;
      }
      return;
    }

    if (changes["points"]) {
      this.syncMarkers();
      this.fitMapToPoints();
      setTimeout(() => this.syncListHeight(), 0);
    }
  }

  ngOnDestroy(): void {
    this.layoutObserver?.disconnect();
    this.mobileLayoutQuery.removeEventListener(
      "change",
      this.onViewportLayoutChange,
    );
    this.map?.remove();
    this.map = null;
    this.markersLayer = null;
    this.markerByPointId.clear();
  }

  writeValue(value: string | null): void {
    this.selectedPointId = value ?? "";
    if (!this.mapInitialized) {
      return;
    }

    this.syncMarkerStyles();
    this.openSelectedPopup();
    this.scrollSelectedIntoView();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  selectPoint(point: CardOrderUpsAccessPoint): void {
    if (this.disabled) {
      return;
    }

    const isNewSelection = this.selectedPointId !== point.id;
    if (isNewSelection) {
      this.selectedPointId = point.id;
      this.onChange(point.id);
      this.onTouched();
      this.syncMarkerStyles();
    }

    this.openPopupForPoint(point.id);
    this.scrollSelectedIntoView();
    this.focusPointOnMap(point);
  }

  isSelected(point: CardOrderUpsAccessPoint): boolean {
    return this.selectedPointId === point.id;
  }

  getPinHtml(selected: boolean, compact = false): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(
      this.buildPinHtml(selected, compact),
    );
  }

  trackByPointId(_index: number, point: CardOrderUpsAccessPoint): string {
    return point.id;
  }

  private setupLayoutSync(): void {
    const mapWrap = this.mapWrap?.nativeElement;
    if (!mapWrap) {
      return;
    }

    this.layoutObserver = new ResizeObserver(() => {
      this.syncListHeight();
    });
    this.layoutObserver.observe(mapWrap);
    this.mobileLayoutQuery.addEventListener(
      "change",
      this.onViewportLayoutChange,
    );
    this.syncListHeight();
  }

  private syncListHeight(): void {
    const list = this.listContainer?.nativeElement;
    const mapWrap = this.mapWrap?.nativeElement;
    if (!list || !mapWrap) {
      return;
    }

    if (this.mobileLayoutQuery.matches) {
      list.style.maxHeight = "320px";
      this.map?.invalidateSize();
      return;
    }

    const mapHeight = mapWrap.getBoundingClientRect().height;
    list.style.maxHeight = mapHeight > 0 ? `${mapHeight}px` : "";
    this.map?.invalidateSize();
  }

  private initMap(): void {
    const container = this.mapContainer?.nativeElement;
    if (!container || this.map) {
      return;
    }

    this.map = L.map(container, {
      zoomControl: true,
      attributionControl: true,
    }).setView(DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.map);

    this.markersLayer = L.layerGroup().addTo(this.map);

    setTimeout(() => {
      this.map?.invalidateSize();
      this.fitMapToPoints();
      this.syncListHeight();
    }, 0);
  }

  private syncMarkers(): void {
    if (!this.map || !this.markersLayer) {
      return;
    }

    this.markersLayer.clearLayers();
    this.markerByPointId.clear();

    for (const point of this.getMappablePoints()) {
      const marker = L.marker([point.latitude, point.longitude], {
        icon: this.createMarkerIcon(false),
        riseOnHover: true,
        interactive: true,
      });

      marker.bindPopup(this.buildPopupHtml(point), {
        closeButton: false,
        offset: [0, -44],
        className: "card-order-access-point__leaflet-popup",
        autoPan: true,
        autoPanPadding: [24, 24],
      });

      marker.on("click", (event) => {
        L.DomEvent.stopPropagation(event);
        this.selectPoint(point);
      });

      marker.addTo(this.markersLayer);
      this.markerByPointId.set(point.id, marker);
    }

    this.syncMarkerStyles();
    this.openSelectedPopup();
  }

  private syncMarkerStyles(): void {
    for (const [pointId, marker] of this.markerByPointId) {
      const selected = this.selectedPointId === pointId;
      marker.setIcon(this.createMarkerIcon(selected));
      marker.setZIndexOffset(selected ? 1000 : 0);
    }
  }

  private openSelectedPopup(): void {
    if (!this.selectedPointId) {
      this.closeAllPopups();
      return;
    }

    this.openPopupForPoint(this.selectedPointId);
  }

  private openPopupForPoint(pointId: string): void {
    const marker = this.markerByPointId.get(pointId);
    if (!marker) {
      return;
    }

    for (const [id, item] of this.markerByPointId) {
      if (id !== pointId) {
        item.closePopup();
      }
    }

    requestAnimationFrame(() => {
      marker.openPopup();
    });
  }

  private closeAllPopups(): void {
    for (const marker of this.markerByPointId.values()) {
      marker.closePopup();
    }
  }

  private createMarkerIcon(selected: boolean): L.DivIcon {
    const size: L.PointTuple = selected ? [38, 48] : [30, 38];
    const anchor: L.PointTuple = selected ? [19, 48] : [15, 38];

    return L.divIcon({
      className: "card-order-access-point__marker",
      html: this.buildPinHtml(selected),
      iconSize: size,
      iconAnchor: anchor,
    });
  }

  private buildPinHtml(selected: boolean, compact = false): string {
    const width = compact ? 22 : 30;
    const height = compact ? 28 : 38;
    const scale = selected && !compact ? "transform:scale(1.12);" : "";
    const svg = this.buildPinSvg(selected).replace(
      "<svg ",
      `<svg width="${width}" height="${height}" `,
    );

    return `<div class="card-order-access-point__pin" style="display:block;transform-origin:center bottom;pointer-events:auto;cursor:pointer;${scale}" aria-hidden="true">${svg}</div>`;
  }

  private buildPinSvg(selected: boolean): string {
    const badgeFill = selected ? "#ffc933" : "#ffb500";
    const badgeStroke = selected ? ' stroke="#ffffff" stroke-width="2"' : "";

    return `
      <svg viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg" focusable="false">
        <path
          fill="rgba(0, 0, 0, 0.18)"
          d="M16 3C9.373 3 4 8.373 4 15c0 9.2 12 22 12 22s12-12.8 12-22c0-6.627-5.373-12-12-12z"
        />
        <path
          fill="${selected ? "#2a150f" : "#351c15"}"
          d="M16 1C8.268 1 2 7.268 2 15c0 10.5 14 25 14 25s14-14.5 14-25C30 7.268 23.732 1 16 1z"
        />
        <circle cx="16" cy="15" r="9.25" fill="${badgeFill}"${badgeStroke} />
        <path
          fill="#fff8ef"
          stroke="#351c15"
          stroke-width="1.2"
          stroke-linejoin="round"
          d="M11.25 12.75h9.5v7.75h-9.5z"
        />
        <path
          fill="none"
          stroke="#351c15"
          stroke-width="1.2"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M11.25 12.75 16 9.75l4.75 3"
        />
        <path
          fill="none"
          stroke="#351c15"
          stroke-width="1.2"
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M16 9.75v10.75"
        />
      </svg>
    `;
  }

  private buildPopupHtml(point: CardOrderUpsAccessPoint): string {
    const distance = formatUpsAccessPointDistanceKm(point.distanceKm);
    const distanceLabel = distance
      ? this.translateText("cardOrders.accessPointDistance", { distance })
      : "";
    const distanceBlock = distanceLabel
      ? `<div class="card-order-access-point__popup-distance">${this.escapeHtml(distanceLabel)}</div>`
      : "";
    const openingHours = this.formatUpsAccessPointOpeningHours(
      point.openingHours,
    );
    const openingHoursBlock = openingHours
      ? `<div class="card-order-access-point__popup-hours"><span class="card-order-access-point__popup-hours-label">${this.escapeHtml(this.translateText("cardOrders.accessPointOpeningHoursLabel"))}</span><span>${this.escapeHtml(openingHours)}</span></div>`
      : "";

    return `
      <div class="card-order-access-point__popup">
        <div class="card-order-access-point__popup-badge">${this.escapeHtml(this.translateText("printCardOrders.accessPoint"))}</div>
        <strong class="card-order-access-point__popup-name">${this.escapeHtml(formatUpsAccessPointName(point.name))}</strong>
        <div class="card-order-access-point__popup-address">${this.escapeHtml(formatUpsAccessPointAddress(point))}</div>
        ${distanceBlock}
        ${openingHoursBlock}
      </div>
    `;
  }

  private translateText(key: string, params?: object): string {
    return this.translate.instant(key, params) as string;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  private fitMapToPoints(): void {
    if (!this.map) {
      return;
    }

    const mappablePoints = this.getMappablePoints();
    if (mappablePoints.length === 0) {
      this.map.setView(DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM);
      return;
    }

    if (mappablePoints.length === 1) {
      const point = mappablePoints[0];
      this.map.setView([point.latitude, point.longitude], 14);
      return;
    }

    const bounds = L.latLngBounds(
      mappablePoints.map((point) => [point.latitude, point.longitude]),
    );

    if (bounds.isValid()) {
      this.map.fitBounds(bounds, { padding: FIT_BOUNDS_PADDING, maxZoom: 15 });
    }
  }

  private focusPointOnMap(point: CardOrderUpsAccessPoint): void {
    if (
      !this.map ||
      point.latitude === undefined ||
      point.longitude === undefined
    ) {
      return;
    }

    const currentZoom = this.map.getZoom();
    const nextZoom = Math.max(currentZoom, 14);
    this.map.setView([point.latitude, point.longitude], nextZoom, {
      animate: true,
    });
  }

  private scrollSelectedIntoView(): void {
    if (!this.selectedPointId) {
      return;
    }

    const list = this.listContainer?.nativeElement;
    if (!list) {
      return;
    }

    const selectedItem = list.querySelector<HTMLElement>(
      `[data-access-point-id="${this.selectedPointId}"]`,
    );
    selectedItem?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  private getMappablePoints(): CardOrderMappableAccessPoint[] {
    return this.points.filter(
      (point): point is CardOrderMappableAccessPoint =>
        point.latitude !== undefined && point.longitude !== undefined,
    );
  }
}
